import { useState, useEffect, useRef, MouseEvent } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "@remo-co/ui-core/src/components/Tooltip";
import { Badge } from "@remo-co/ui-core/src/components/Badge";
import { makeSelectUserStatusByUserId } from "modules/userStatus/redux/selectors";
import { selectCurrentEvent } from "modules/event/selectors";
import { selectUser } from "modules/auth/redux/selectors";
import { useI18n } from "i18n";
import { useParticipant } from "modules/audioVideo/hooks/useParticipant";
import { IRoom } from "types";
import { isEventManager } from "modules/event/utils/eventUtils";
import { getMapTemplateByType } from "modules/event/template";
import { AssignedTableNudgePopover } from "modules/assignTables/components";
import { createIsUserChangingRoomByUserId } from "store/rooms";
import {
  CONTEXT_MENU_TYPES,
  SEAT_BORDER_WIDTH,
  SEAT_RADIUS,
  USER_AVAILABILITY_COLORS,
} from "modules/event";
import Avatar from "modules/userAvatar/Avatar";
import { makeSelectUserById } from "modules/event/usersInEvent/selectors";
import { selectTheaterTemplate } from "modules/theater/selectors";
import { useContextMenu } from "modules/contextMenu";
import classnames from "classnames";
import useUserProblemReporter from "modules/userProblemReporter/hooks/useUserProblemReporter";
import { getUserName } from "modules/userProfile";
import { openUserProfileCard } from "modules/userProfileCard/redux/slice";
import { useAppDispatch } from "store/hooks";
import { selectUserActivityStatus } from "modules/eventActivityStatus/redux/selectors";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { ActivityStatus } from "modules/eventActivityStatus/types";
import { selectIsNetworkingRecommendationUser } from "modules/networkingRecommendations/redux/selectors";
import { selectRightPanelState, TABS } from "modules/rightPanel";
import useStyles from "./styles";
import { SoundWavesAnimation } from "../SoundWavesAnimation";
import { AvatarStatus } from "../AvatarStatus";
import useMissingUserInSpace from "../useMissingUserInSpace";
import { UserLocatingAnimation } from "../UserLocatingAnimation";

interface Props {
  x: number;
  y: number;
  room: IRoom;
  userId: string;
  isHoveringOnTable: boolean;
}

export const MapAvatarComponent = ({
  x,
  y,
  room,
  userId,
  isHoveringOnTable,
}: Props): JSX.Element | null => {
  const { audio, video } = useParticipant({ userId });
  const isMicAndCamOff = !audio && !video;
  const spaceConfigData = useSelector(selectTheaterTemplate);
  const spaceConfig = getMapTemplateByType(spaceConfigData);
  const userStatus = useSelector(makeSelectUserStatusByUserId(userId));
  const userInSpace = useSelector(makeSelectUserById(userId));
  const currentUser = useSelector(selectUser);
  const currentEvent = useSelector(selectCurrentEvent);
  const eventActivityStatus = useSelector(selectUserActivityStatus(userId));
  const isUserChangingRoom = useSelector(
    createIsUserChangingRoomByUserId(userId),
  );
  const { isOpen: rightPanelIsOpen, tabId: rightPanelTabId } = useSelector(
    selectRightPanelState,
  );
  const { t } = useI18n(["common", "event", "userLocator"]);
  const avatarRef = useRef<HTMLElement | null>(null);
  const [contextMenu, setContextMenu] = useState<JSX.Element | undefined>();
  const { openContextMenu } = useContextMenu();
  const { checkUserProblems } = useUserProblemReporter();
  const [openTooltip, setOpenTooltip] = useState(false);
  const [tooltipBlocked, setTooltipBlocked] = useState(false);
  const [tooltipText, setTooltipText] = useState("");
  const { handleMissingAvatar } = useMissingUserInSpace();
  const classes = useStyles({ status: eventActivityStatus?.status ?? null });
  const dispatch = useAppDispatch();
  const isNetWorkingUser = useSelector(
    selectIsNetworkingRecommendationUser(userId),
  );

  const isTalking = userStatus && userStatus.get("isTalking");

  const handleTooltipClose = () => {
    setOpenTooltip(false);
  };

  const handleTooltipOpen = () => {
    setOpenTooltip(true);
  };

  const isItMe = currentUser?.id === userId;
  const amIAdmin = isEventManager(currentEvent ?? undefined, userId);

  useEffect(() => {
    if (!userInSpace) {
      return;
    }

    let status = t(`event:activity.status.${ActivityStatus.ACTIVE}`);
    if (eventActivityStatus) {
      status =
        eventActivityStatus.status === eventActivityStatus.label
          ? t(`event:activity.status.${eventActivityStatus.status}`)
          : eventActivityStatus.label;
    }

    const userStatusTitle = t("event:click.view.more.name", {
      key: `${getUserName(userInSpace)} ${
        amIAdmin ? `(${t("event:host")})` : ""
      }`,
      status: `\n${status}`,
    });

    setTooltipText(userStatusTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInSpace, userStatus, eventActivityStatus]);

  if (!currentUser || !userInSpace || !currentEvent) {
    handleMissingAvatar(
      currentUser,
      userInSpace,
      currentEvent,
      userId,
      room.id,
    );

    return null;
  }

  const handleRightClick = async (e: MouseEvent) => {
    e.stopPropagation();

    const menu = await openContextMenu(
      e.clientX,
      e.clientY,
      CONTEXT_MENU_TYPES.USER,
      userId,
      room.id,
    );

    if (!menu) return;

    setContextMenu(menu);
  };

  const onClick = () => {
    dispatch(openUserProfileCard({ userId, anchorEl: avatarRef }));
  };

  const borderWidth = SEAT_BORDER_WIDTH;
  const r = spaceConfig?.seatRadius ?? SEAT_RADIUS;
  const containerStyle = {
    left: x,
    top: y,
    width: `${(r + borderWidth) * 2}px`,
    height: `${(r + borderWidth) * 2}px`,
    cursor: "pointer",
  };
  const hasMicCamIssues = checkUserProblems(userId);

  return (
    <>
      <Tooltip
        placement="top"
        title={
          <Typography variant="caption" className={classes.tooltipText}>
            {tooltipText}
          </Typography>
        }
        open={openTooltip && !tooltipBlocked}
        arrow
        onClose={handleTooltipClose}
        onOpen={handleTooltipOpen}
      >
        <div
          onContextMenu={handleRightClick}
          id={isItMe ? "map-av-me" : `map-av-${userId}`}
          className={classes.mapAvatarContainer}
          data-testid={getUserName(userInSpace)}
          style={containerStyle}
        >
          <div>
            <UserLocatingAnimation
              currentUser={currentUser}
              userInSpace={userInSpace}
              userId={userId}
              onAnimationStart={() => {
                setTooltipBlocked(true);
              }}
              onAnimationEnd={() => {
                setTooltipBlocked(false);
              }}
            />
            {!isMicAndCamOff && isTalking && isHoveringOnTable && (
              <SoundWavesAnimation />
            )}
            <AvatarStatus
              loginUser={currentUser}
              userStatus={userStatus}
              event={currentEvent}
              userInSpace={userInSpace}
              isItMe={isItMe}
              isAdmin={amIAdmin}
            />
            <div
              className={classnames(classes.borderEffect, {
                [classes.isNetworking]:
                  isNetWorkingUser &&
                  rightPanelIsOpen &&
                  rightPanelTabId === TABS.NETWORKING_RECOMMENDATIONS,
              })}
            />
            <div
              className={classnames(classes.avatarBorder, {
                [classes.animate]: isUserChangingRoom,
                [classes.grayedOut]: isMicAndCamOff,
              })}
              style={{ background: USER_AVAILABILITY_COLORS.AVAILABLE }}
            />
            {userInSpace && (
              <Badge
                overlap="circular"
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                variant="dot"
                className={classes.activityBadge}
              >
                <Avatar
                  data-testid="map-avatar"
                  ref={avatarRef}
                  user={userInSpace}
                  className={classes.mapAvatar}
                  isAdmin={amIAdmin}
                  hasMicCamIssues={hasMicCamIssues}
                  onClick={onClick}
                  showEmoji
                />
              </Badge>
            )}
          </div>
        </div>
      </Tooltip>
      {contextMenu}
      {isItMe && <AssignedTableNudgePopover avatarRef={avatarRef} />}
    </>
  );
};
